import { startOfDayInTimezone } from '@apps/web/lib/date_utils';
import { type ContactActivityItem } from '@apps/web/swr_data_fetching/contact_activity';

/**
 * Groups a contact's activity items into Podium-style recency buckets, always
 * evaluated against the organization's timezone calendar days (not the
 * browser's local time). Buckets are emitted newest-first with a stable key,
 * an uppercase label, and a count.
 *
 * Bucket rules (by whole tz-calendar days between the item and `now`):
 * - 0 days      → TODAY
 * - 1 day       → YESTERDAY
 * - 2–6 days    → THIS WEEK
 * - 7–29 days   → "{n} WEEK[S] AGO" where n = floor(days / 7) (1–4)
 * - ≥ 30 days   → "{n} MONTH[S] AGO" where n = max(1, floor(days / 30))
 *
 * `now` is an explicit parameter so unit tests stay deterministic.
 */

export type ContactActivityBucket = {
    readonly key: string;
    readonly label: string;
    readonly count: number;
    readonly items: ReadonlyArray<ContactActivityItem>;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

type BucketDescriptor = {
    readonly key: string;
    readonly label: string;
    // Sort rank, ascending = more recent. Used to order the emitted buckets.
    readonly order: number;
};

// Whole tz-calendar days between an item and `now`. Both operands are the UTC
// instant of their tz calendar day's midnight, so the difference is a clean
// multiple of a day (rounding absorbs DST-shortened/lengthened days).
function calendarDaysAgo(
    occurredAt: string,
    now: Date,
    timeZone: string,
): number {
    const startNowMs = startOfDayInTimezone(now, timeZone).getTime();
    const startItemMs = startOfDayInTimezone(
        new Date(occurredAt),
        timeZone,
    ).getTime();
    return Math.round((startNowMs - startItemMs) / MS_PER_DAY);
}

function bucketDescriptorFor(daysAgo: number): BucketDescriptor {
    if (daysAgo <= 0) {
        return { key: 'today', label: 'TODAY', order: 0 };
    }
    if (daysAgo === 1) {
        return { key: 'yesterday', label: 'YESTERDAY', order: 1 };
    }
    if (daysAgo <= 6) {
        return { key: 'this-week', label: 'THIS WEEK', order: 2 };
    }
    if (daysAgo < 30) {
        const weeks = Math.floor(daysAgo / 7);
        return {
            key: `weeks-${weeks}`,
            label: `${weeks} ${weeks === 1 ? 'WEEK' : 'WEEKS'} AGO`,
            order: 100 + weeks,
        };
    }
    const months = Math.max(1, Math.floor(daysAgo / 30));
    return {
        key: `months-${months}`,
        label: `${months} ${months === 1 ? 'MONTH' : 'MONTHS'} AGO`,
        order: 1000 + months,
    };
}

export function bucketContactActivity(
    items: ReadonlyArray<ContactActivityItem>,
    timeZone: string,
    now: Date,
): ReadonlyArray<ContactActivityBucket> {
    const sorted = [...items].sort(
        (a, b) =>
            new Date(b.occurred_at).getTime() -
            new Date(a.occurred_at).getTime(),
    );

    const buckets = new Map<
        string,
        { label: string; order: number; items: Array<ContactActivityItem> }
    >();
    for (const item of sorted) {
        const descriptor = bucketDescriptorFor(
            calendarDaysAgo(item.occurred_at, now, timeZone),
        );
        const existing = buckets.get(descriptor.key);
        if (existing) {
            existing.items.push(item);
        } else {
            buckets.set(descriptor.key, {
                label: descriptor.label,
                order: descriptor.order,
                items: [item],
            });
        }
    }

    return [...buckets.entries()]
        .sort(([, a], [, b]) => a.order - b.order)
        .map(([key, value]) => ({
            key,
            label: value.label,
            count: value.items.length,
            items: value.items,
        }));
}
